import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Loader2, Award, RefreshCw } from "lucide-react";

export default function ROIProof() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/roi-proof");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="roi-proof-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading ROI proof…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="roi-proof">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-indigo-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">ROI Proof</h3>
          <span className="text-xs text-slate-500">live · renewal-ready</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="roi-proof-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
        <div className="rounded-md p-2.5 bg-indigo-50 border border-indigo-200" data-testid="roi-value">
          <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Value captured</div>
          <div className="text-base font-semibold text-indigo-900 mt-0.5">{fmtGBP(data.valueCapturedGBP)}</div>
        </div>
        <div className="rounded-md p-2.5 bg-emerald-50 border border-emerald-200" data-testid="roi-recovered">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Recovered</div>
          <div className="text-base font-semibold text-emerald-900 mt-0.5">{fmtGBP(data.recoveredGBP)}</div>
        </div>
        <div className="rounded-md p-2.5 bg-amber-50 border border-amber-200" data-testid="roi-leaks">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Leaks found</div>
          <div className="text-base font-semibold text-amber-900 mt-0.5">{fmtGBP(data.leaksFoundGBP)}</div>
        </div>
        <div className="rounded-md p-2.5 bg-rose-50 border border-rose-200" data-testid="roi-gift-aid">
          <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Gift Aid unlocked</div>
          <div className="text-base font-semibold text-rose-900 mt-0.5">{fmtGBP(data.giftAidUnlockedGBP)}</div>
        </div>
        <div className="rounded-md p-2.5 bg-slate-50 border border-slate-200" data-testid="roi-hours">
          <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Admin hours saved</div>
          <div className="text-base font-semibold text-slate-900 mt-0.5">{data.adminHoursSaved || 0}h</div>
        </div>
        <div className="rounded-md p-2.5 bg-slate-50 border border-slate-200" data-testid="roi-reports">
          <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Reports dispatched</div>
          <div className="text-base font-semibold text-slate-900 mt-0.5">{data.reportsGenerated || 0}</div>
        </div>
      </div>

      <ul className="space-y-1 text-xs text-slate-700 list-disc pl-4" data-testid="roi-summary">
        {(data.summaryLines || []).slice(0, 5).map((l, i) => <li key={i}>{l}</li>)}
      </ul>
    </Card>
  );
}
